import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Scale, Plus, FileText, DollarSign, Building2, TrendingUp, ChevronRight, Clock, Hash, Calendar, Activity, BookOpenText, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/App";
import { toast } from "sonner";
import TopNav from "@/components/TopNav";

const fmt = (v) => new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(v || 0);

const MONTO_KEYS = [
  { key: "monto_principal", label: "Principal", color: "#5b9a8b" },
  { key: "costo_inversion", label: "Inversión", color: "#7ba7c9" },
  { key: "lucro_cesante", label: "Lucro Cesante", color: "#c4a265" },
  { key: "dano_moral", label: "Daño Moral", color: "#c98a9a" },
  { key: "reajuste_ipc", label: "Reajuste IPC", color: "#6ba8b5" },
  { key: "costas_procesales", label: "Costas", color: "#8a9bae" },
];

function Dashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [casos, setCasos] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [valData, setValData] = useState(null);
  const [showFinancial, setShowFinancial] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("todos");
  const [eventosRecientes, setEventosRecientes] = useState([]);
  const [showHistoria, setShowHistoria] = useState(false);
  const [demandadosList, setDemandadosList] = useState([]);
  const [selectedDemandado, setSelectedDemandado] = useState("");
  const [exportingHistoria, setExportingHistoria] = useState(false);
  const [exportingDossier, setExportingDossier] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [casosRes, empresasRes, valRes, eventosRes] = await Promise.all([
        api.get("/casos"), api.get("/empresas"), api.get("/dashboard/valorizacion-total"),
        api.get("/eventos/recientes?limit=6")
      ]);
      setCasos(casosRes.data);
      setEmpresas(empresasRes.data);
      setValData(valRes.data);
      setEventosRecientes(eventosRes.data || []);
    } catch { toast.error("Error cargando datos"); }
    finally { setLoading(false); }
  };

  const openHistoriaModal = async () => {
    try {
      const res = await api.get("/demandados/con-casos");
      setDemandadosList(res.data || []);
      setShowHistoria(true);
    } catch { toast.error("Error cargando demandados"); }
  };

  const handleExportHistoria = async () => {
    if (!selectedDemandado) { toast.error("Selecciona un demandado"); return; }
    setExportingHistoria(true);
    toast.info("Generando historia completa con análisis IA, puede tardar 60-90 segundos…");
    try {
      const res = await api.get(`/demandados/${selectedDemandado}/historia/pdf`, { responseType: "blob", timeout: 180000 });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      const dem = demandadosList.find(d => d.id === selectedDemandado);
      const safe = (dem?.nombre_legal || "demandado").replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 50);
      link.download = `Historia_Completa_${safe}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Historia completa descargada");
      setShowHistoria(false);
      setSelectedDemandado("");
    } catch (err) { toast.error(err.response?.data?.detail || "Error generando historia"); }
    finally { setExportingHistoria(false); }
  };

  const handleExportDossier = async () => {
    setExportingDossier(true);
    toast.info("Generando dossier completo (PDF + adjuntos), puede tardar 1-2 minutos…");
    try {
      const res = await api.get("/exportar/dossier-completo", { responseType: "blob", timeout: 300000 });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const link = document.createElement("a");
      link.href = url;
      const ts = new Date().toISOString().replace(/[:.\-T]/g, "").slice(0, 14);
      link.download = `LegalStory_Dossier_Completo_${ts}.zip`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Dossier completo descargado");
    } catch (err) { toast.error(err.response?.data?.detail || "Error generando dossier"); }
    finally { setExportingDossier(false); }
  };

  const casosFiltrados = casos.filter(c => {
    const matchSearch = !searchTerm || c.titulo_caso?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchEstado = filtroEstado === "todos" || c.estado_caso === filtroEstado;
    return matchSearch && matchEstado;
  });

  const empresaMap = {};
  empresas.forEach(e => { empresaMap[e.id] = e.nombre_legal; });

  const enPrep = casos.filter(c => c.estado_caso === "en_preparacion").length;
  const presentados = casos.filter(c => c.estado_caso === "presentado").length;
  const enProceso = casos.filter(c => c.estado_caso === "en_revision").length;

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg)" }}>
      <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: "var(--primary)" }} />
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }} data-testid="dashboard-page">
      <TopNav user={user} onLogout={onLogout} />

      <main className="container mx-auto px-6 py-6 max-w-6xl">
        {/* KPI Row - Centrado y mejorado */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8" data-testid="metrics-section">
          {/* Total Casos */}
          <div className="ls-metric text-center" data-testid="kpi-total-casos">
            <div className="w-10 h-10 rounded-xl mx-auto mb-3 flex items-center justify-center" style={{ background: "var(--primary-light)" }}>
              <Scale className="h-5 w-5" style={{ color: "var(--primary)" }} />
            </div>
            <p className="text-3xl font-bold mb-1" style={{ color: "var(--text)" }} data-testid="total-cases-metric">{casos.length}</p>
            <p className="text-[11px] font-medium uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Total Casos</p>
          </div>

          {/* En Preparación */}
          <div className="ls-metric text-center" data-testid="kpi-en-prep">
            <div className="w-10 h-10 rounded-xl mx-auto mb-3 flex items-center justify-center" style={{ background: "var(--accent-amber-light)" }}>
              <Clock className="h-5 w-5" style={{ color: "var(--accent-amber)" }} />
            </div>
            <p className="text-3xl font-bold mb-1" style={{ color: "var(--text)" }} data-testid="prep-cases-metric">{enPrep}</p>
            <p className="text-[11px] font-medium uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>En Preparación</p>
          </div>

          {/* En Proceso */}
          <div className="ls-metric text-center" data-testid="kpi-en-proceso">
            <div className="w-10 h-10 rounded-xl mx-auto mb-3 flex items-center justify-center" style={{ background: "var(--accent-blue-light)" }}>
              <FileText className="h-5 w-5" style={{ color: "var(--accent-blue)" }} />
            </div>
            <p className="text-3xl font-bold mb-1" style={{ color: "var(--text)" }}>{enProceso}</p>
            <p className="text-[11px] font-medium uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>En Proceso</p>
          </div>

          {/* Presentados */}
          <div className="ls-metric text-center" data-testid="kpi-presentados">
            <div className="w-10 h-10 rounded-xl mx-auto mb-3 flex items-center justify-center" style={{ background: "var(--accent-purple-light)" }}>
              <Building2 className="h-5 w-5" style={{ color: "var(--accent-purple)" }} />
            </div>
            <p className="text-3xl font-bold mb-1" style={{ color: "var(--text)" }}>{presentados}</p>
            <p className="text-[11px] font-medium uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Presentados</p>
          </div>

          {/* Valorización Total */}
          <button
            onClick={() => setShowFinancial(true)}
            className="ls-metric text-center group cursor-pointer col-span-2 md:col-span-1"
            style={{ background: "var(--primary-light)", borderColor: "var(--primary-muted)" }}
            data-testid="kpi-valorizacion"
          >
            <div className="w-10 h-10 rounded-xl mx-auto mb-3 flex items-center justify-center" style={{ background: "var(--primary)" }}>
              <DollarSign className="h-5 w-5 text-white" />
            </div>
            <p className="text-lg font-bold mb-1" style={{ color: "var(--primary-dark)" }} data-testid="total-valorizacion-metric">
              {valData ? fmt(valData.gran_total) : "$0"}
            </p>
            <div className="flex items-center justify-center gap-1">
              <p className="text-[11px] font-medium uppercase tracking-wider" style={{ color: "var(--primary-dark)" }}>Valorización</p>
              <TrendingUp className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: "var(--primary)" }} />
            </div>
          </button>
        </div>

        {/* Últimos hechos agregados */}
        {eventosRecientes.length > 0 && (
          <div className="ls-card p-5 mb-6" data-testid="recent-events-panel">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4" style={{ color: "var(--primary)" }} />
                <h3 className="text-sm font-bold" style={{ color: "var(--text)" }}>Últimos hechos agregados</h3>
              </div>
              <span className="text-[10px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>actividad reciente</span>
            </div>
            <div className="grid md:grid-cols-2 gap-2">
              {eventosRecientes.map((ev) => (
                <button
                  key={ev.id}
                  onClick={() => navigate(`/casos/${ev.caso_id}`)}
                  className="text-left px-3 py-2.5 rounded-lg transition-colors hover:bg-[var(--bg)] border"
                  style={{ borderColor: "var(--border-light)", background: "var(--surface)" }}
                  data-testid={`recent-event-${ev.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-medium truncate" style={{ color: "var(--text)" }}>{ev.titulo_evento}</p>
                      <p className="text-[10px] truncate mt-0.5" style={{ color: "var(--text-muted)" }}>
                        Caso #{ev.caso_numero} · {ev.caso_titulo}
                      </p>
                    </div>
                    <span className="text-[10px] shrink-0 px-1.5 py-0.5 rounded" style={{ background: "var(--bg)", color: "var(--text-muted)" }}>
                      {ev.tipo_evento}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Cases Section */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 mb-4">
          <h2 className="text-base font-bold" style={{ color: "var(--text)" }}>
            Casos <span className="text-xs font-normal ml-1 px-2 py-0.5 rounded-md" style={{ background: "var(--bg)", color: "var(--text-muted)" }}>{casosFiltrados.length}</span>
          </h2>
          <div className="flex items-center gap-2 w-full md:w-auto">
            <Input placeholder="Buscar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="h-8 text-xs w-full md:w-44" style={{ background: "var(--surface)", borderColor: "var(--border)" }} data-testid="search-input" />
            <Select value={filtroEstado} onValueChange={setFiltroEstado}>
              <SelectTrigger className="h-8 w-32 text-xs" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="en_preparacion">En preparación</SelectItem>
                <SelectItem value="en_revision">En revisión</SelectItem>
                <SelectItem value="presentado">Presentado</SelectItem>
                <SelectItem value="cerrado">Cerrado</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={openHistoriaModal} size="sm" variant="outline" className="h-8 text-xs shrink-0" data-testid="historia-completa-btn">
              <BookOpenText className="h-3.5 w-3.5 mr-1" /> Historia completa
            </Button>
            <Button onClick={handleExportDossier} disabled={exportingDossier} size="sm" variant="outline" className="h-8 text-xs shrink-0" title="ZIP con PDF, Markdown para IA y adjuntos por caso" data-testid="export-dossier-btn">
              <BookOpenText className="h-3.5 w-3.5 mr-1" /> {exportingDossier ? "Generando…" : "Dossier IA"}
            </Button>
            <Button onClick={() => navigate("/casos/nuevo")} size="sm" className="ls-btn-primary h-8 text-xs shrink-0" data-testid="new-case-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> Nuevo
            </Button>
          </div>
        </div>

        <div className="space-y-2" data-testid="cases-list">
          {casosFiltrados.length === 0 ? (
            <div className="ls-card p-12 text-center" style={{ borderStyle: "dashed" }}>
              <Scale className="h-8 w-8 mx-auto mb-3" style={{ color: "var(--text-light)" }} />
              <p className="text-sm mb-3" style={{ color: "var(--text-muted)" }}>No hay casos registrados</p>
              <Button onClick={() => navigate("/casos/nuevo")} size="sm" className="ls-btn-primary text-xs">
                <Plus className="h-3.5 w-3.5 mr-1" /> Crear caso
              </Button>
            </div>
          ) : casosFiltrados.map(caso => {
            const casoTotal = caso.valorizacion ? Object.values(caso.valorizacion).reduce((a, b) => a + (typeof b === "number" ? b : 0), 0) : 0;
            const badgeClass = { en_preparacion: "ls-badge-pending", en_revision: "ls-badge-pending", presentado: "ls-badge-done", cerrado: "ls-badge-closed" }[caso.estado_caso] || "ls-badge-closed";

            return (
              <div key={caso.id} onClick={() => navigate(`/casos/${caso.id}`)} className="ls-card px-5 py-4 cursor-pointer group" data-testid={`case-card-${caso.id}`}>
                <div className="flex items-center gap-4">
                  {/* Número de caso */}
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: "var(--primary-light)" }}>
                    <span className="text-sm font-bold" style={{ color: "var(--primary)" }} data-testid={`case-number-${caso.id}`}>
                      #{caso.numero_caso || "—"}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <h3 className="text-sm font-semibold truncate" style={{ color: "var(--text)" }}>{caso.titulo_caso}</h3>
                      <span className={`ls-badge ${badgeClass}`}>{caso.estado_caso?.replace("_", " ")}</span>
                      {caso.tipo_demanda && <span className="ls-badge" style={{ background: "var(--accent-blue-light)", color: "var(--accent-blue)" }}>{caso.tipo_demanda}</span>}
                    </div>
                    <div className="flex items-center gap-3 text-[11px]" style={{ color: "var(--text-muted)" }}>
                      <span>{empresaMap[caso.empresa_cliente_id] || "—"}</span>
                      <span>&middot;</span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(caso.fecha_creacion).toLocaleDateString("es-CL")}
                      </span>
                      {caso.abogados_asignados?.length > 0 && (
                        <><span>&middot;</span><span>{caso.abogados_asignados.length} abogado{caso.abogados_asignados.length > 1 ? "s" : ""}</span></>
                      )}
                    </div>
                  </div>
                  {casoTotal > 0 && (
                    <div className="text-right shrink-0">
                      <p className="text-[10px]" style={{ color: "var(--text-muted)" }}>Valorización</p>
                      <p className="text-sm font-bold" style={{ color: "var(--primary)" }}>{fmt(casoTotal)}</p>
                    </div>
                  )}
                  <ChevronRight className="h-4 w-4 shrink-0 transition-colors" style={{ color: "var(--text-light)" }} />
                </div>
              </div>
            );
          })}
        </div>
      </main>

      {/* Financial Dialog */}
      <Dialog open={showFinancial} onOpenChange={setShowFinancial}>
        <DialogContent className="max-w-2xl max-h-[75vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4" style={{ color: "var(--primary)" }} /> Resumen Financiero
            </DialogTitle>
          </DialogHeader>
          {valData && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-2">
                {MONTO_KEYS.map(m => (
                  <div key={m.key} className="p-3 rounded-lg" style={{ background: "var(--bg)" }}>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-2 h-2 rounded-full" style={{ background: m.color }} />
                      <span className="text-[10px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{m.label}</span>
                    </div>
                    <p className="text-sm font-bold" style={{ color: "var(--text)" }}>{fmt(valData.totales?.[m.key])}</p>
                  </div>
                ))}
              </div>
              <div className="p-4 rounded-lg flex items-center justify-between" style={{ background: "var(--primary-light)" }}>
                <span className="text-sm font-medium" style={{ color: "var(--primary-dark)" }}>Gran Total</span>
                <span className="text-2xl font-bold" style={{ color: "var(--primary-dark)" }}>{fmt(valData.gran_total)}</span>
              </div>
              <table className="w-full ls-table">
                <thead><tr><th>#</th><th>Caso</th><th>Estado</th><th className="text-right">Total</th></tr></thead>
                <tbody>
                  {valData.casos?.map(c => (
                    <tr key={c.id} className="cursor-pointer" onClick={() => { setShowFinancial(false); navigate(`/casos/${c.id}`); }}>
                      <td className="font-medium" style={{ color: "var(--primary)" }}>{c.numero_caso || "—"}</td>
                      <td className="font-medium">{c.titulo_caso}</td>
                      <td><span className={`ls-badge ${c.estado_caso === "presentado" ? "ls-badge-done" : "ls-badge-pending"}`}>{c.estado_caso?.replace("_", " ")}</span></td>
                      <td className="text-right font-bold" style={{ color: "var(--primary)" }}>{fmt(c.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Historia Completa por Demandado */}
      <Dialog open={showHistoria} onOpenChange={setShowHistoria}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpenText className="h-5 w-5" style={{ color: "var(--primary)" }} />
              Historia completa por demandado
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-xs" style={{ color: "var(--text-secondary)" }}>
              Selecciona una empresa demandada y se generará un PDF estructurado con: línea de tiempo consolidada de todas sus causas, historia narrada con foco en patrones, red flags detectadas, delitos eventuales, qué hay que probar y pruebas adicionales a recopilar. Útil para que quien alimente datos pueda agregar nuevas pruebas e ir entendiendo la historia.
            </p>
            <div>
              <label className="text-xs font-medium" style={{ color: "var(--text-secondary)" }}>Empresa demandada</label>
              <Select value={selectedDemandado} onValueChange={setSelectedDemandado}>
                <SelectTrigger className="h-9 text-sm" data-testid="historia-demandado-select"><SelectValue placeholder="Selecciona un demandado…" /></SelectTrigger>
                <SelectContent>
                  {demandadosList.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-gray-500">Sin demandados con causas activas</div>
                  ) : demandadosList.map(d => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.nombre_legal} {d.rut ? `· ${d.rut}` : ""} · {d.casos_count} caso{d.casos_count !== 1 ? "s" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowHistoria(false)}>Cancelar</Button>
              <Button onClick={handleExportHistoria} disabled={exportingHistoria || !selectedDemandado} className="ls-btn-primary" data-testid="historia-export-confirm-btn">
                {exportingHistoria ? <><RefreshCw className="h-4 w-4 mr-1 animate-spin" />Generando…</> : <><FileText className="h-4 w-4 mr-1" />Generar y descargar PDF</>}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Dashboard;
